/**
 * 用户生命周期 · **契约层**（2026-10-07 · Lead 授权新建）。
 *
 * 背景（用户反馈）：「权限管理怎么不能增加用户，也没有角色？」—— C1 只做了"给已存在用户配权限"，
 * **缺少创建用户与改角色**。本文件是这条链路的**唯一口径来源**（零依赖纯函数 + 常量）。
 *
 * 硬口径（Lead 裁定）：
 *  1. **角色域只有三个**：`admin | manager | viewer`（DB CHECK `users_role_chk`，
 *     `packages/db/migrations/0007_accounts.sql:65`；store 的 `AuthRole` 改为**引用本文件**，
 *     不允许出现第二份枚举）。⚠️ 前端曾默认写 `"member"` —— 那是**非法值**，
 *     要靠前端修，**不要为迁就它新增第四种角色**；
 *  2. **只有超管能授予/变更 `admin`**：普通管理员不能创建 admin、也不能把任何人提升为 admin，
 *     更不能改自己（"不能改自己"由既有 `canManageUser` 在服务端强制）；
 *  3. **口令策略不在这里**：口令强度/哈希由 `@ams/store` 的 `hashPassword` 负责（scrypt +
 *     DB CHECK `password_hash LIKE 'scrypt$%'`）—— 契约层**不复制**该策略，避免两份真相；
 *  4. **`createdBy` / `branchPath` 由服务端生成**（`createUser` 按父用户路径拼接），
 *     客户端**不可传**；本文件的 payload 里刻意**没有**这两个字段。
 *  5. ⭐ **2026-10-08 修正**：上一条"角色域只有三个"**只适用于历史遗留的 `users.role` 列**
 *     （DB CHECK 仍在）。RBAC 的**角色集合是数据**（`roles` 表，含自定义角色）⇒
 *     `role` 字段的取值是**角色名**，契约只判**形状**，**存在性由服务端查表**。
 */

import { ROLE_NAME_MAX_LENGTH } from "./roles.js";

/** 角色域（**内置**三角色；`store` 的 `AuthRole` 引用它 = 历史遗留 `users.role` 的合法域）。 */
export const USER_ROLES = ["admin", "manager", "viewer"] as const;

export type UserRole = (typeof USER_ROLES)[number];

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === "string" && (USER_ROLES as readonly string[]).includes(value);
}

/**
 * ⭐ **角色名形状校验（唯一实现）** —— 2026-10-08 事故修复：**角色集合是数据，不是常量**。
 *
 * 病根：`role` 曾被写死成 `admin|manager|viewer` 三值白名单 ⇒ 用户新建的**自定义角色
 * 根本无法指派**（前端能选、提交被服务端拒）。
 * ⇒ 契约层只判**形状**（非空 / 无首尾空白 / ≤ `ROLE_NAME_MAX_LENGTH` / 无控制字符）；
 * **存在性**由服务端查 `roles` 表判定（未知 ⇒ 400 + 把可用角色名列出来）。
 *
 * ⛔ **不许**在这里（或任何契约函数里）再放一份角色白名单。
 */
export function roleNameShapeIssue(raw: unknown): UserPayloadRejection | null {
  if (typeof raw !== "string") {
    return { field: "role", code: "invalid_type", message: "角色名必须是字符串" };
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { field: "role", code: "required", message: "角色不能为空" };
  }
  if (trimmed !== raw) {
    return { field: "role", code: "untrimmed", message: "角色名不能带首尾空白" };
  }
  if (trimmed.length > ROLE_NAME_MAX_LENGTH) {
    return { field: "role", code: "too_long", message: `角色名不能超过 ${ROLE_NAME_MAX_LENGTH} 个字符` };
  }
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) {
    return { field: "role", code: "control_char", message: "角色名不能包含控制字符" };
  }
  return null;
}

/** 超管判定（与 `@ams/contracts` 的 `isSuperAdmin` 同口径：只看 `admin`）。 */
export function isSuperAdminRole(role: string): boolean {
  return role === "admin";
}

/**
 * **角色授予规则**（纯函数，服务端必须调用；前端禁用按钮只是体验）。
 *
 * ## ⚠️ 与"功能档位"的关系：**两套机制叠加（AND），不是替代**（Lead 2026-10-07 要求写明）
 *
 * 执行任何写操作（含用户管理）必须**同时**满足两层门槛：
 *  1. **功能档位**（C1 三档，见 `packages/contracts/src/feature-access.ts`）：该功能点必须是 `write`
 *     —— 回答"**你能不能碰这个功能**"；
 *  2. **角色下限**（本文件）—— 回答"**你在管理链上处于什么位置**"：
 *     · 角色管理 ⇒ **至少 `manager`**（`viewer` 一律不能管用户，**即使**它被授了
 *       `settings_permissions: write`；与用户裁决"受限=只读"一致）；
 *     · 授予/变更 `admin` ⇒ **必须是 `admin`**（普通管理员连创建 admin 都不行）。
 *
 * ⇒ 因此会出现"**授了 `settings_permissions: write` 为什么还是不能操作**"——档位只管第 1 层。
 * **两层分开报错**（功能档位不足 ⇒ 403 功能；角色下限不足 ⇒ 403 角色），
 * **不要**合并成一句"权限不足"，否则排障要猜是哪一层。
 *
 * ### 为什么"必须分开"（判据 · `docs/HANDOVER.md` §7-65）
 * > **"需要不同补救动作的错误，必须在响应里优先暴露"**。
 *
 * 两者的**补救动作完全不同**：
 *  · 档位不足 ⇒ 去找管理员**给这个人加该功能点的 `write` 档**；
 *  · 角色下限不足 ⇒ **换一个有管理角色的人来操作**（给 `viewer` 加档位永远救不了）。
 * 合并成一句"权限不足"会让用户**照着错误结论行动**：他加了档位、发现还是不行、再回来问 ——
 * 与本会话已确立的另外三条同一取向：**配置无效优先于业务结果**、**非法参数先于权限短路**、
 * **装配错误优先于权限拒绝**。
 *
 * · 角色值**形状非法** ⇒ `false`（fail closed，不静默放行）。
 * · ⭐ 2026-10-08：**"这个名字存在吗"不在这里判**（角色集合是数据）⇒ 由服务端查 `roles`；
 *   未知角色名 ⇒ **400**（不是 403）—— 与"非法参数先于权限短路"同一取向。
 */
export function canAssignRole(input: { readonly actorRole: string; readonly nextRole: string }): boolean {
  if (roleNameShapeIssue(input.nextRole) !== null) return false;
  if (input.nextRole === "admin") return isSuperAdminRole(input.actorRole);
  return input.actorRole === "manager" || isSuperAdminRole(input.actorRole);
}

/** 角色变更时的人话拒绝原因（403/400 的 `message`；不含任何用户标识）。 */
export const ROLE_ASSIGNMENT_DENIED_MESSAGE = "只有超级管理员可以授予或变更「管理员」角色";

// ---------------------------------------------------------------------------
// 请求/响应形状
// ---------------------------------------------------------------------------

/** `POST /v1/permissions/users` 的请求体。**没有 `createdBy`/`branchPath`**（服务端生成）。 */
export interface CreateUserPayload {
  readonly username: string;
  /**
   * 初始口令（明文只在请求里；服务端 `hashPassword` 后落库，**响应绝不回显**）。
   *
   * ⭐ 2026-10-08（用户裁定「新增用户时**自动生成口令**」）：**可选**。
   *  · 不给 ⇒ 服务端生成高强度一次性口令，响应以 `generatedPassword` **仅此一次**返回，
   *    并置 `must_change_password=true`（该用户首次登录必须先改密）；
   *  · 给了 ⇒ 沿用（**不置**强制改密，保持既有语义）；空串仍是**非法**（不是"没给"）。
   *
   * ⚠️ 这里只管"形状"；**口令强度策略的唯一实现是 store 的 `hashPassword`**（长度 8–1024），
   * 生成侧也必须过它，**不得绕开**。
   */
  readonly password?: string;
  /**
   * **角色名**（内置 `admin|manager|viewer` 或**自定义角色名**）。
   *
   * ⭐ 2026-10-08：类型从 `UserRole`（三值联合）放宽为 `string` —— 角色集合是**数据**
   *（`roles` 表）。契约只校验**形状**；**存在性由服务端查 `roles` 表**（未知 ⇒ 400 +
   * `details` 里列出可用角色名），**绝不静默写 NULL**（那正是"零权限"事故的成因）。
   */
  readonly role: string;
}

/** `PATCH /v1/permissions/users/:userId` 的请求体（至少给一个字段）。 */
export interface UpdateUserPayload {
  /** 改成哪个**角色名**（同 `CreateUserPayload.role`：形状在此、存在性在服务端）。 */
  readonly role?: string;
  /** `false` = 停用（服务端**同事务撤销该用户全部会话**）；`true` = 启用。 */
  readonly isActive?: boolean;
  /** 重置口令（服务端 `hashPassword`；响应绝不回显）。 */
  readonly password?: string;
}

/** 创建/更新后的用户摘要（**无口令/哈希字段**）。 */
export interface PermissionUserSummary {
  readonly userId: string;
  readonly username: string;
  readonly role: UserRole;
  readonly isActive: boolean;
  readonly createdBy: string | null;
  readonly createdAt: string;
}

// ---------------------------------------------------------------------------
// 入参形状校验（只做"形状"，不做口令强度/分支/角色越权 —— 那些各有归属）
// ---------------------------------------------------------------------------

export interface UserPayloadRejection {
  readonly field: string;
  readonly code: string;
  /** 人话（后台/UI 直接逐条显示，不吞成"保存失败"）。 */
  readonly message: string;
}

export type UserPayloadValidation =
  | { readonly ok: true }
  | { readonly ok: false; readonly rejections: readonly UserPayloadRejection[] };

const USERNAME_MAX_LENGTH = 64;

/**
 * `POST` 的形状校验：`username` 非空且 ≤64、`password` **缺省或用非空串**、`role` ∈ 三角色。
 *
 * ⭐ 2026-10-08：`password` 缺省 = **合法**（服务端自动生成）；`""` 仍然非法
 *（"没给"与"给了空串"是两件事，前端传空输入框时必须能定位到字段）。
 */
export function validateCreateUserPayload(payload: CreateUserPayload): UserPayloadValidation {
  const rejections: UserPayloadRejection[] = [];
  const username = payload.username.trim();
  if (username === "") {
    rejections.push({ field: "username", code: "required", message: "用户名不能为空" });
  } else if (username.length > USERNAME_MAX_LENGTH) {
    rejections.push({
      field: "username",
      code: "too_long",
      message: `用户名最长 ${String(USERNAME_MAX_LENGTH)} 个字符`,
    });
  }
  if (payload.password !== undefined && typeof payload.password !== "string") {
    rejections.push({ field: "password", code: "invalid_type", message: "初始口令必须是字符串" });
  } else if (payload.password === "") {
    rejections.push({
      field: "password",
      code: "required",
      message: "初始口令不能为空（不给该字段则服务端自动生成）",
    });
  }
  // ⭐ 角色只判形状；**存在性由服务端查 `roles`**（自定义角色必须可指派）。
  const roleIssue = roleNameShapeIssue(payload.role);
  if (roleIssue !== null) rejections.push(roleIssue);
  return rejections.length === 0 ? { ok: true } : { ok: false, rejections };
}

/** `PATCH` 的形状校验：至少给一个字段；`role` 若给只判**形状**（存在性在服务端查 `roles`）。 */
export function validateUpdateUserPayload(payload: UpdateUserPayload): UserPayloadValidation {
  const rejections: UserPayloadRejection[] = [];
  if (payload.role === undefined && payload.isActive === undefined && payload.password === undefined) {
    rejections.push({ field: "body", code: "no_changes", message: "至少提供一个要修改的字段（role / isActive / password）" });
  }
  if (payload.role !== undefined) {
    const roleIssue = roleNameShapeIssue(payload.role);
    if (roleIssue !== null) rejections.push(roleIssue);
  }
  if (payload.isActive !== undefined && typeof payload.isActive !== "boolean") {
    rejections.push({ field: "isActive", code: "invalid_type", message: "isActive 必须是布尔值" });
  }
  if (payload.password !== undefined && payload.password === "") {
    rejections.push({ field: "password", code: "required", message: "重置口令不能为空" });
  }
  return rejections.length === 0 ? { ok: true } : { ok: false, rejections };
}
